import { Capacitor } from '@capacitor/core'

/**
 * A stable identifier for this installation — one per browser profile or
 * desktop app install, unlike CLIENT_ID which is per tab/session.
 *
 * Kept in localStorage so the same machine keeps one entry in the device list
 * across restarts rather than accumulating a row per launch.
 */
export const DEVICE_ID: string = (() => {
  const KEY = 'indigonotes.deviceId'
  try {
    const existing = localStorage.getItem(KEY)
    if (existing) return existing
    const fresh = crypto.randomUUID()
    localStorage.setItem(KEY, fresh)
    return fresh
  } catch {
    // Private mode / storage blocked: ephemeral id, a new row each launch.
    return crypto.randomUUID()
  }
})()

const isDesktopShell = typeof window !== 'undefined' && 'electronShell' in window

/**
 * Ask Capacitor rather than sniffing the URL scheme. With
 * `androidScheme: 'https'` the app is served from https://localhost, so a
 * `capacitor:` protocol check is always false and the native app was being
 * labelled as a browser.
 */
const isCapacitor = Capacitor.isNativePlatform()

function osName(ua: string): string {
  if (/Android/i.test(ua)) return 'Android'
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS'
  if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS'
  if (/Windows/i.test(ua)) return 'Windows'
  if (/Linux/i.test(ua)) return 'Linux'
  return 'Unknown OS'
}

function browserName(ua: string): string {
  if (/Edg\//.test(ua)) return 'Edge'
  if (/OPR\//.test(ua)) return 'Opera'
  if (/Firefox\//.test(ua)) return 'Firefox'
  if (/Chrome\//.test(ua)) return 'Chrome'
  if (/Safari\//.test(ua)) return 'Safari'
  return 'Browser'
}

/** Human label for the device list, e.g. "Indigo Notes for macOS" or "Chrome on macOS". */
export function describeDevice(): { label: string; platform: string } {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  if (isCapacitor) {
    const native = Capacitor.getPlatform() === 'ios' ? 'iOS' : 'Android'
    return { label: `Indigo Notes for ${native}`, platform: `${native} app` }
  }
  const os = osName(ua)
  if (isDesktopShell) return { label: `Indigo Notes for ${os}`, platform: `${os} app` }
  return { label: `${browserName(ua)} on ${os}`, platform: `${os} web` }
}

export function relativeTime(ms: number): string {
  const diff = Date.now() - ms
  if (diff < 60_000) return 'Active now'
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins} min ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`
  return new Date(ms).toLocaleDateString()
}
